import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CouponsAPI } from '../../../api/payments/coupons/api';
import useFeatureFlag from '../../../hooks/useFeatureFlag';
import { FEATURE_FLAGS } from '../../../constants/FeatureFlag';
import { getInitialParams } from './utils';

export function useCouponsBootstrap() {
  const canAccess = useFeatureFlag(
    FEATURE_FLAGS.KILL_SWITCH.ADMIN_PAYMENTS_ARCH_V2,
  );

  const history = useHistory();
  const location = useLocation();
  const initialParams = useRef(getInitialParams(location.search)).current;
  const isFirstRender = useRef(true);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(initialParams.perPage);
  const [filterText, setFilterText] = useState(initialParams.code);
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);

  const updateQueryParams = (params) => {
    const searchParams = new URLSearchParams(location.search);
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        searchParams.set(key, value);
      } else {
        searchParams.delete(key);
      }
    });
    history.replace({ search: searchParams.toString() });
  };

  const fetchCoupons = async (
    pageToFetch,
    rowsPerPage = perPage,
    code = filterText,
  ) => {
    setLoading(true);
    try {
      const response = await CouponsAPI.list({
        page: pageToFetch - 1,
        limit: rowsPerPage,
        code,
      });
      const { content, totalElements } = response.data;
      setData(content || []);
      setTotalRows(totalElements || 0);
    } catch (error) {
      toast.warn('Erro ao carregar cupons!');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    updateQueryParams({ page: newPage });
    fetchCoupons(newPage);
  };

  const handlePerRowsChange = async (newPerPage, newPage) => {
    setPerPage(newPerPage);
    updateQueryParams({ perPage: newPerPage, page: newPage });
    await fetchCoupons(newPage, newPerPage);
  };

  const handleAddCouponButton = () => {
    history.push('/payments/coupons/add');
  };

  const handleFilterChange = (event) => setFilterText(event.target.value);

  const handleClearFilter = () => {
    if (filterText) {
      setResetPaginationToggle(!resetPaginationToggle);
      setFilterText('');
    }
  };

  useEffect(() => {
    if (!canAccess.flagResolved || !canAccess.isFeatureFlagEnable) return;
    fetchCoupons(initialParams.page, initialParams.perPage, initialParams.code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccess.flagResolved, canAccess.isFeatureFlagEnable]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const timeoutId = setTimeout(() => {
      updateQueryParams({ page: 1, code: filterText });
      fetchCoupons(1, perPage, filterText);
    }, 400);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterText]);

  return {
    canAccess,
    data,
    loading,
    totalRows,
    perPage,
    filterText,
    resetPaginationToggle,
    initialParams,
    handlePageChange,
    handlePerRowsChange,
    handleAddCouponButton,
    handleFilterChange,
    handleClearFilter,
  };
}
